import { KeyboardEvent, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  Box,
  Button,
  Grid,
  IconButton,
  InputLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import moment from 'moment';

import { CfsApi, KaseApi, UserApi } from '@/api';
import { AIIcon, IcClose } from '@/assets/images';
import {
  FormRoot,
  RenderFormField,
  SelectItem,
  TableContainer,
} from '@/components';
import { withLoader, WithLoaderProps } from '@/hocs';
import withUnsavedChangesWarning, {
  WithUnsavedChangesWarningProps,
} from '@/hocs/withUnsavedChangesWarning';
import { useKaseContext } from '@/hooks/useKaseContext';
import { IKaseNewForm, KaseTypes, Role } from '@/models';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { colors } from '@/theme/variables';
import { formatPersonName } from '@/utils';

import { KaseFormFields } from '../KaseList/data';
import LocationCodeSearch from '../LocationCodeSearch/LocationCodeSearch';

interface IKasesFormProps extends WithLoaderProps {
  cfsId?: string;
  defaultCtvCode?: string;
  onClose: () => void;
}

const KasesForm = ({
  showLoader,
  hideLoader,
  cfsId,
  onClose,
  setIsDirty,
  onNavigation,
  defaultCtvCode,
}: IKasesFormProps & WithUnsavedChangesWarningProps) => {
  const { kase, updateKase } = useKaseContext();
  const { user } = useUserStore();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { updateToast } = useToastStore();
  const methods = useForm<IKaseNewForm>({
    mode: 'all',
  });
  const latestCfsRequestId = useRef(0);
  const [officerOptions, setOfficerOptions] = useState<SelectItem[]>([]);
  const [editorOfficerOptions, setEditorOfficerOptions] = useState<
    SelectItem[]
  >([]);
  const [cfsAiSummary, setCfsAiSummary] = useState<string>('');

  const {
    handleSubmit,
    reset,
    setValue,
    formState,
    watch,
    getValues,
    getFieldState,
    trigger,
  } = methods;

  const kaseTypeOptions = useMemo(() => {
    return Object.entries(KaseTypes).map(([value, label]) => ({
      label,
      value,
    }));
  }, []);

  const currentAssignedOfficers = watch('assistingOfficers');
  const currentPrimaryOfficer = watch('primaryOfficer');
  const hasOfficerSection = Boolean(
    currentPrimaryOfficer || currentAssignedOfficers?.length,
  );

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchCfs();
  }, [cfsId]);

  useEffect(() => {
    resetKaseFormValues();
  }, [kase, onClose]);

  useEffect(() => {
    if (kase || !user?._id) return;

    const hasPoliceRole =
      user.roles.findIndex(
        (userRole) => userRole.role === Role.RMS_CASES_FULL_ACCESS,
      ) > -1;
    if (hasPoliceRole) {
      setValue('primaryOfficer', user?._id);
    }
  }, [!kase, setValue, user?.roles]);

  const primaryOfficerOptions = useMemo<SelectItem[]>(() => {
    const officer = kase?.primaryOfficer;
    if (!officer?._id) return editorOfficerOptions;
    if (editorOfficerOptions.some((o) => o.value === officer._id)) {
      return editorOfficerOptions;
    }
    const label = formatPersonName(officer) || officer._id;
    return [...editorOfficerOptions, { value: officer._id, label }];
  }, [editorOfficerOptions, kase?.primaryOfficer]);

  useEffect(() => {
    if (!currentPrimaryOfficer) return;
    const currentMap = getValues('requireNarative') as
      | Record<string, boolean>
      | undefined;
    if (!currentMap?.[currentPrimaryOfficer]) return;
    const { [currentPrimaryOfficer]: _omitted, ...rest } = currentMap;
    setValue('requireNarative', rest, { shouldDirty: true });
  }, [currentPrimaryOfficer, getValues, setValue]);

  useEffect(() => {
    if (!currentPrimaryOfficer) return;
    if (!currentAssignedOfficers?.includes(currentPrimaryOfficer)) return;
    setValue(
      'assistingOfficers',
      currentAssignedOfficers.filter(
        (officerId) => officerId !== currentPrimaryOfficer,
      ),
      { shouldDirty: true },
    );
  }, [currentAssignedOfficers, currentPrimaryOfficer, setValue]);

  const watchedIncidentStartedAt = watch('incidentStartedAt');
  const watchedIncidentReportedAt = watch('incidentReportedAt');

  useEffect(() => {
    const errored = (
      ['incidentStartedAt', 'incidentReportedAt'] as const
    ).filter((field) => getFieldState(field).error);
    if (errored.length) trigger([...errored]);
  }, [
    watchedIncidentStartedAt,
    watchedIncidentReportedAt,
    getFieldState,
    trigger,
  ]);

  const fetchCfs = async () => {
    if (!cfsId) {
      latestCfsRequestId.current += 1;
      return;
    }

    const requestId = ++latestCfsRequestId.current;
    showLoader();
    try {
      const { data } = await CfsApi.getOne(cfsId);
      if (requestId !== latestCfsRequestId.current) return;

      const cfsPrimaryOfficer = data.primaryOfficer
        ? typeof data.primaryOfficer === 'string'
          ? data.primaryOfficer
          : (data.primaryOfficer as { _id: string })?._id
        : undefined;
      const primaryOfficerId = getValues('primaryOfficer');
      const currentLocationCode = getValues('locationCode');

      reset({
        address: data.addressInfo?.address,
        type: 'INCIDENT_REPORT',
        incidentReportedAt: data.createdAt,
        primaryOfficer: cfsPrimaryOfficer ?? primaryOfficerId,
        assistingOfficers: data.assignedUnitShifts.flatMap(
          (assignedUnitShift) =>
            assignedUnitShift.unitShift.users?.flatMap(
              (userResource) => userResource._id,
            ) ?? [],
        ),
        requireNarative: {},
        incidentCodes: data.incidentCodes.map(({ _id }) => _id),
        locationCode: currentLocationCode,
      });

      try {
        const aiSummaryResponse = await CfsApi.generateAISummary(cfsId);
        if (requestId !== latestCfsRequestId.current) return;
        setCfsAiSummary(aiSummaryResponse.data?.shortDescription || '');
      } catch {
        // AI summary not available, leave empty
        if (requestId !== latestCfsRequestId.current) return;
        setCfsAiSummary('');
      }
    } catch (err: any) {
      if (requestId !== latestCfsRequestId.current) return null;
      updateToast({ open: true, message: err.message });
      return null;
    } finally {
      hideLoader();
    }
  };

  const resetKaseFormValues = () => {
    if (!kase) return;

    reset({
      _id: kase._id,
      primaryInvestigator: kase.primaryInvestigator?._id,
      assistingOfficers: kase.assistingOfficers?.map((item) => item._id),
      primaryOfficer: kase.primaryOfficer?._id,
      address: kase.address,
      type: kase.type,
      incidentCodes: kase.incidentCodes,
      locationCode: kase.locationCode?._id,
      sdpIdentifier: kase.sdpIdentifier || undefined,
      incidentStartedAt: kase.incidentStartedAt
        ? new Date(kase.incidentStartedAt?.toString())
        : undefined,
      ...(kase.incidentReportedAt && {
        incidentReportedAt: new Date(kase.incidentReportedAt?.toString()),
      }),
      incidentStartedAtOption: kase.incidentStartedAtOption || undefined,
      incidentReportedAtTimeOption: kase.incidentReportedAtTimeOption,
      ...(kase.incidentEndedAt && {
        incidentEndedAt: new Date(kase.incidentEndedAt.toString()),
      }),
      requireNarative: kase?.requireNarative?.reduce(
        (acc, item) =>
          item === kase.primaryOfficer?._id ? acc : { ...acc, [item]: true },
        {},
      ),
    });
  };

  const fetchEmployees = async () => {
    showLoader();
    const toOptions = (results: { _id: string; fullName: string }[]) =>
      results.map((officer) => ({
        value: officer._id,
        label: officer.fullName,
      }));
    try {
      const [editors, allOfficers] = await Promise.all([
        UserApi.get({
          limit: 1000,
          filter: JSON.stringify({
            'roles.role': { $in: ['RMS_CASES_FULL_ACCESS'] },
          }),
        }),
        UserApi.get({
          limit: 1000,
          filter: JSON.stringify({
            'roles.role': {
              $in: ['RMS_CASES_FULL_ACCESS', 'RMS_CASES_VIEW'],
            },
          }),
        }),
      ]);
      setEditorOfficerOptions(toOptions(editors.data.results));
      setOfficerOptions(toOptions(allOfficers.data.results));
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    } finally {
      hideLoader();
    }
  };

  const getListItems = (fieldName: string) => {
    if (fieldName === 'type') return kaseTypeOptions;
    if (fieldName === 'primaryOfficer') return primaryOfficerOptions;
    return officerOptions;
  };

  const onSubmit = async (values: IKaseNewForm) => {
    if (values.address && !values.address._id) {
      updateToast({
        open: true,
        message: 'Please wait for the address to finish loading.',
        type: ConfirmTypes.WARNING,
      });
      return false;
    }

    setIsDirty(false);
    showLoader();

    const officerList = officerOptions.map((officer) => officer.value);
    try {
      const primaryOfficerId = values.primaryOfficer ?? '';
      const data = {
        ...values,
        primaryOfficer: primaryOfficerId,
        primaryInvestigator: values.primaryInvestigator ?? '',
        address: values.address?._id ?? '',
        incidentStartedAt: values.incidentStartedAt
          ? moment(values.incidentStartedAt).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
          : '',
        assistingOfficers:
          values.assistingOfficers?.filter((assistingOfficer) =>
            officerList.includes(assistingOfficer),
          ) ?? [],
        incidentReportedAt: values.incidentReportedAt
          ? moment(values.incidentReportedAt).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
          : '',
        incidentEndedAt: values.incidentEndedAt
          ? moment(values.incidentEndedAt).format('YYYY-MM-DDTHH:mm:ss.SSSZ')
          : '',
        requireNarative: values.requireNarative
          ? Object.entries(values.requireNarative).reduce(
              (acc, [key, value]) => (value ? [...acc, key] : acc),
              [] as string[],
            )
          : [],
      };

      const res = cfsId
        ? await KaseApi.createFromCsf(data, cfsId)
        : await KaseApi.save(data);

      if (res.data._id) {
        updateToast({
          open: true,
          message: kase ? 'Changes saved successfully!' : 'New case saved',
          type: ConfirmTypes.SUCCESS,
        });
        if (values._id) {
          updateKase(res.data);
        } else {
          const navRules =
            values.type !== 'INCIDENT_REPORT' ? '/narrative' : '/overview';
          navigate(`/cases/${res.data._id}${navRules}`);
          window.location.reload(); // reload the page to reflect the new case
        }
      } else {
        updateToast({
          open: true,
          message: 'Error: cannot save case',
          type: ConfirmTypes.ERROR,
        });
      }
    } catch (err: any) {
      updateToast({ open: true, message: err.message });
    }
    hideLoader();
    reset();
    onClose();
    return false;
  };

  return (
    <FormProvider {...methods}>
      <FormRoot>
        <Grid
          container
          flexDirection="row"
          columnSpacing={2}
          justifyContent="flex-start"
          mb={1}
        >
          {KaseFormFields.map(
            ({ grid, type, name, handleChange, ...rest }: any) => (
              <Grid
                key={name}
                {...grid}
                item
                mb={2}
                sx={{
                  maxWidth: 300,
                  ...(searchParams?.get('fieldKey') === name && {
                    boxShadow: '0 0 10px 0px #900',
                  }),
                }}
                // className={handleReportClasses(
                //   name,
                //   searchParams?.get('fieldKey'),
                // )}
              >
                {name === 'locationCode' ? (
                  <>
                    <InputLabel>Location code of Incident</InputLabel>
                    <LocationCodeSearch
                      onSelect={(value) => {
                        setIsDirty(true);
                        setValue('locationCode', value);
                      }}
                      value={kase?.locationCode}
                      agencyCtvCodeId={defaultCtvCode}
                    />
                  </>
                ) : // Primary officer should be pre-populated with current user (if applicable) on Creating Case
                name === 'primaryOfficer' && !kase ? (
                  <RenderFormField
                    type="autocomplete"
                    name="primaryOfficer"
                    label="Primary officer"
                    items={getListItems('primaryOfficer')}
                    handleChange={() => {
                      setIsDirty(true);
                      if (handleChange) {
                        handleChange('primaryOfficer');
                      }
                    }}
                    {...rest}
                  />
                ) : (
                  <RenderFormField
                    {...rest}
                    name={name}
                    type={type}
                    {...((type === 'select' || type === 'autocomplete') && {
                      items:
                        name !== 'primaryOfficer'
                          ? getListItems(name)
                          : getListItems(name).filter(
                              (officer) =>
                                !currentAssignedOfficers?.includes(
                                  officer.value as string,
                                ),
                            ),
                      handleChange: () => {
                        setIsDirty(true);
                        if (handleChange) {
                          handleChange(name);
                        }
                      },
                    })}
                    {...(type === 'datetimeselect' && {
                      onKeyUp: (e: KeyboardEvent<HTMLInputElement>) => {
                        e.stopPropagation();
                        if (e.key === 'Enter') {
                          setValue(name, new Date());
                        }
                      },
                      cfsId,
                    })}
                    MenuProps={{
                      PaperProps: { sx: { maxHeight: 250, padding: 0 } },
                    }}
                  />
                )}
              </Grid>
            ),
          )}
        </Grid>
        <Grid container>
          <Grid item md={12}>
            <RenderFormField
              label="Responding officers"
              name="assistingOfficers"
              type="autocomplete"
              items={officerOptions?.filter(
                (officer) => currentPrimaryOfficer !== officer.value,
              )}
              multiple
              limitTags={3}
            />
          </Grid>
          {hasOfficerSection && (
            <Grid item md={12}>
              <TableContainer
                dontCalculateHeight
                sx={{
                  padding: 0,
                  boxShadow: 'none',
                  borderRadius: 0,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell key="Assisting officer" colSpan={2} />
                      <TableCell
                        key="Require narrative"
                        sx={{ textAlign: 'right' }}
                      >
                        Require narrative
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {currentPrimaryOfficer &&
                      officerOptions.map((officerOption) => {
                        const userId = officerOption.value;
                        return (
                          officerOption.value === currentPrimaryOfficer && (
                            <TableRow key={userId}>
                              <TableCell sx={{ width: '40px' }}>
                                <IconButton
                                  onClick={() =>
                                    setValue(
                                      'assistingOfficers',
                                      currentAssignedOfficers?.filter(
                                        (officer) => officer !== userId,
                                      ),
                                    )
                                  }
                                  sx={{
                                    color: colors.error.main,
                                    alignSelf: 'center',
                                  }}
                                >
                                  <IcClose />
                                </IconButton>
                              </TableCell>
                              <TableCell>
                                <Typography fontSize="0.925rem">
                                  {officerOption.label}
                                </Typography>
                              </TableCell>
                              <TableCell
                                sx={{ textAlign: 'right', height: '47px' }}
                              >
                                <Switch
                                  color="error"
                                  checked={!!currentPrimaryOfficer}
                                  disabled
                                  sx={{ mr: 2 }}
                                />
                              </TableCell>
                            </TableRow>
                          )
                        );
                      })}
                    {currentAssignedOfficers?.map((userID) => {
                      const officerDetails = officerOptions.find(
                        (officer) => officer.value === userID,
                      );

                      return (
                        officerDetails && (
                          <TableRow key={userID}>
                            <TableCell sx={{ width: '40px' }}>
                              <IconButton
                                onClick={() =>
                                  setValue(
                                    'assistingOfficers',
                                    currentAssignedOfficers?.filter(
                                      (officer) => officer !== userID,
                                    ),
                                  )
                                }
                                sx={{
                                  color: colors.error.main,
                                  alignSelf: 'center',
                                }}
                              >
                                <IcClose />
                              </IconButton>
                            </TableCell>
                            <TableCell>
                              <Typography fontSize="0.925rem">
                                {officerDetails.label}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ textAlign: 'right' }}>
                              <RenderFormField
                                name={`requireNarative.${userID}`}
                                type="switch"
                                checked={
                                  !!kase?.requireNarative?.includes(userID)
                                }
                              />
                            </TableCell>
                          </TableRow>
                        )
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          )}
        </Grid>
        {cfsId && (
          <Box sx={{ mt: 2, width: '100%' }}>
            <InputLabel
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}
            >
              <AIIcon style={{ width: 16, height: 16 }} />
              CFS AI Summary
            </InputLabel>
            <TextField
              value={cfsAiSummary}
              disabled
              fullWidth
              size="small"
              sx={{
                width: '100%',
                '& .MuiInputBase-input.Mui-disabled': {
                  cursor: 'not-allowed',
                },
              }}
            />
          </Box>
        )}
      </FormRoot>
      <Stack
        justifyContent="flex-end"
        flexDirection="row"
        sx={{ position: 'sticky', bottom: 0, pt: 2 }}
      >
        <Button
          variant="contained"
          color="inherit"
          size="medium"
          onClick={() => {
            if (onNavigation(new Event('close'))) {
              onClose();
            }
          }}
          sx={{ mx: 2 }}
        >
          Cancel
        </Button>
        <Button
          color="error"
          variant="contained"
          size="medium"
          type="submit"
          onClick={handleSubmit(onSubmit)}
          disabled={formState.isSubmitting}
        >
          {kase ? 'Save' : 'Create'}
        </Button>
      </Stack>
    </FormProvider>
  );
};

export default withLoader(withUnsavedChangesWarning(KasesForm));
